import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import InfiniteJobDropdown from './InfiniteJobDropdown';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Referral = () => {
  const location = useLocation();
  const { action, jobTitle, department, location: jobLocation, description } = location.state || {};
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    skills: '',
    experience: '',
    positionApplied: jobTitle || '',
    department: department || '',
    location: jobLocation || '',
    description: description || '',
    noticePeriod: '',
    referralreason: '',
    referrerName: '',
    referrerEmail: '',
    referrerRelation: ''
  });
const [selectedJobDetails, setSelectedJobDetails] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingReferrerInfo, setLoadingReferrerInfo] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Determine if this is an application or referral
  const isApplication = action === 'apply';
  const isReferral = action === 'refer' || !action;

  const [companyCountry, setCompanyCountry] = useState('India');

  const fetchCompanyCountry = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.company_id) {
        const { data } = await axios.get(`${API_URL}/api/cmp/${payload.company_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (data.success && data.data?.country) {
          setCompanyCountry(data.data.country);
        }
      }
    } catch (error) {
      // console.error("Error fetching company country:", error);
    }
  };

  useEffect(() => {
    fetchCompanyCountry();
    if (isApplication) {
      setLoadingReferrerInfo(false);
    } else {
      fetchReferrerInfo();
    }
    fetchAvailableJobs();
  }, [isApplication]);

  const fetchReferrerInfo = async () => {
    try {
      setLoadingReferrerInfo(true);

      const sessionDebug = await axios.get(`${API_URL}/api/refer/debug-session`, {
        withCredentials: true,
        headers:{
          Authorization:`Bearer ${localStorage.getItem('token')}`

        }
      });
       const token = localStorage.getItem('token'); 
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/refer/referrer/info`,  {
        headers: {
          Authorization: `Bearer ${token}`,
       
        },
        withCredentials: true,
      });
      
      setFormData(prevFormData => ({
        ...prevFormData,
        referrerName: response.data.referrerName || '',
        referrerEmail: response.data.referrerEmail || ''
      }));
    } catch (error) {
      //console.error('Error fetching referrer info:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch your information. Please fill in your details manually.',
        severity: 'warning'
      });
    } finally {
      setLoadingReferrerInfo(false);
    }
  };

const fetchAvailableJobs = async () => {
  try {
    setLoadingJobs(true);
    const token = localStorage.getItem('token'); 
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    const response = await axios.get(`${API_URL}/api/jobpost/list`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    
    const openJobs = response.data.jobs.filter(job => {
      const closingDate = new Date(job.jobClosedDate);
      const today = new Date();
      return closingDate > today;
    });
    
    setAvailableJobs(openJobs);
    
    // If we came from job details page, auto-select that job
    if (jobTitle) {
      const matchedJob = openJobs.find(job => job.jobTitle === jobTitle);
      if (matchedJob) {
        setSelectedJobDetails(matchedJob);
        setFormData(prev => ({
          ...prev,
          positionApplied: matchedJob.jobTitle,
          department: matchedJob.department,
          location: matchedJob.location,
          description: matchedJob.description
        }));
      }
    }
  } catch (error) {
    //console.error("Error fetching jobs:", error);
    setSnackbar({
      open: true,
      message: "Failed to fetch available positions. Please try again.",
      severity: "error",
    });
  } finally {
    setLoadingJobs(false);
  }
};

  const isValidPhoneNumber = (phone) => {
    if (companyCountry.toLowerCase() !== 'india') return phone && phone.trim().length >= 7;
    return phone && /^[6-9]\d{9}$/.test(phone.replace(/\s+/g, ''));
  };

  const isValidEmail = (email) => {
    return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateFile = (file) => {
    if (!file) {
      return 'Resume is required';
    }

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/rtf',
      'text/rtf',
      'text/plain',
      'application/vnd.oasis.opendocument.text'
    ];
    
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.rtf', '.txt', '.odt'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
    
    if (!isValidType) {
      return 'Please upload PDF, DOC, DOCX, RTF, TXT, or ODT files only';
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return 'File size must be less than 10MB';
    }

    if (file.name.length > 100) {
      return 'File name is too long (maximum 100 characters)';
    }

    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(file.name)) {
      return 'File name contains invalid characters';
    }

    return '';
  };

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'phone':
        if (!isValidPhoneNumber(value)) {
          error = companyCountry.toLowerCase() === 'india' 
            ? 'Please enter a valid 10-digit phone number starting with 6-9'
            : 'Please enter a valid phone number';
        }
        break;
      case 'email':
        if (!isValidEmail(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'referrerEmail':
        if (isReferral && !isValidEmail(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'name':
        if (!value || value.trim().length < 2) {
          error = 'Name must be at least 2 characters long';
        }
        break;
      case 'experience':
        if (!value || value < 0) {
          error = 'Please enter valid years of experience';
        }
        break;
      case 'positionApplied':
        if (!value) {
          error = 'Please select a position';
        }
        break;
      case 'referrerName':
        if (isReferral && (!value || value.trim().length < 2)) {
          error = 'Your name must be at least 2 characters long';
        }
        break;
      case 'referralreason':
        if (isReferral && (!value || value.trim().length < 10)) {
          error = 'Referral reason must be at least 10 characters long';
        }
        break;
      case 'referrerRelation':
        if (isReferral && (!value || value.trim().length < 3)) {
          error = 'Relationship must be at least 3 characters long';
        }
        break;
      default:
        break;
    }
    
    return error;
  };

const handleChange = (e) => {
  const { name, value } = e.target;
  
  // Special handling for positionApplied to auto-fill department and location
  if (name === 'positionApplied') {
    const selectedJob = availableJobs.find(job => job.jobTitle === value);
    
    if (selectedJob) {
      setSelectedJobDetails(selectedJob);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        department: selectedJob.department || '',
        location: selectedJob.location || '',
        description: selectedJob.description || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      setSelectedJobDetails(null);
    }
  } else {
    setFormData({
      ...formData,
      [name]: value
    });
  }

  const error = validateField(name, value);
  setValidationErrors(prev => ({
    ...prev,
    [name]: error
  }));
};

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (selectedFile) {
      const error = validateFile(selectedFile);
      setFileError(error);
      
      if (!error) {
        setFile(selectedFile);
      } else {
        setFile(null);
        e.target.value = '';
      }
    } else {
      setFile(null);
      setFileError('');
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = ['name', 'email', 'phone', 'experience', 'positionApplied', 'department', 'location'];
    
    if (isReferral) {
      requiredFields.push('referralreason', 'referrerName', 'referrerEmail', 'referrerRelation');
    }

    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
      }
    });

    const fileValidationError = validateFile(file);
    if (fileValidationError) {
      setFileError(fileValidationError);
      errors.file = fileValidationError;
    } else {
      setFileError('');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Please fix the validation errors before submitting.",
        severity: "error"
      });
      return;
    }
  
    setLoading(true);
  
    try {
      const formDataToSend = new FormData();
      
      // Add form fields individually with proper handling
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('phone', formData.phone.trim());
      formDataToSend.append('experience', formData.experience.toString());
      formDataToSend.append('positionApplied', formData.positionApplied);
      formDataToSend.append('department', formData.department);
      formDataToSend.append('location', formData.location);
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('noticePeriod', formData.noticePeriod || '');
      formDataToSend.append('applicationType', isApplication ? 'application' : 'referral');
      
      // Handle skills array properly
      const skillsArray = formData.skills 
        ? formData.skills.split(',').map(skill => skill.trim()).filter(skill => skill !== '')
        : [];
      formDataToSend.append('skills', JSON.stringify(skillsArray));
      
      // Add referral-specific fields only for referrals
      if (isReferral) {
        formDataToSend.append('referralreason', formData.referralreason.trim());
        formDataToSend.append('referrerName', formData.referrerName.trim());
        formDataToSend.append('referrerEmail', formData.referrerEmail.trim());
        formDataToSend.append('referrerRelation', formData.referrerRelation.trim());
      }
      
      // Add file if present
      if (file) {
        formDataToSend.append('resume', file);
      }
  
      // Get company_id from token (like payroll)
      const token = localStorage.getItem('token');
      let company_id = '';
      if (token) {
        const payloadToken = JSON.parse(atob(token.split('.')[1]));
        company_id = payloadToken.company_id;
      }
  

  
      const endpoint = isApplication 
        ? `${API_URL}/api/refer/apply` 
        : `${API_URL}/api/refer/create`;
  
      const response = await axios.post(endpoint, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
        timeout: 30000 // 30 second timeout
      });
      
      setSnackbar({
        open: true,
        message: isApplication ? 'Application submitted successfully!' : 'Candidate referred successfully!',
        severity: 'success'
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        skills: '',
        experience: '',
        positionApplied: jobTitle || '',
        department: department || '',
        location: jobLocation || '',
        description: description || '',
        noticePeriod: '',
        referralreason: '',
        referrerName: isApplication ? '' : formData.referrerName,
        referrerEmail: isApplication ? '' : formData.referrerEmail,
        referrerRelation: ''
      });
      setValidationErrors({});
      setFile(null);
      setFileError('');
      
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      //console.error('Error submitting form:', error);
      
      let errorMessage = `Failed to ${isApplication ? 'submit application' : 'create referral'}`;
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please try again.';
      } else if (error.message.includes('Network Error')) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <Container maxWidth="1500px" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ 
        p: 4,  
        borderRadius: 2,
        border: '2px solid',
        borderColor: 'grey.300',
        boxShadow: 4  
      }}>
        <Typography variant="h4"  gutterBottom fontWeight="bold">
          {isApplication ? 'Apply for Job' : 'Refer a Candidate'}
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2 }}>
            {isApplication ? 'Your Information' : 'Candidate Information'}
          </Typography>

          <Grid container spacing={3}>
          <Grid item xs={12}>
  <TextField
    fullWidth
    required
    id="name"
    label="Full Name"
    name="name"
    value={formData.name}
    onChange={(e) => {
      const value = e.target.value;
      if (/^[a-zA-Z\s]*$/.test(value)) {
        handleChange(e);
      }
    }}
    error={!!validationErrors.name}
    helperText={validationErrors.name}
  />
</Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="email"
                required
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!validationErrors.email}
                helperText={validationErrors.email}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
             <TextField
  fullWidth
  required
  id="phone"
  label="Phone Number"
  name="phone"
  value={formData.phone}
  onChange={(e) => {
    const value = e.target.value;

    if (/^\+?[0-9]{0,15}$/.test(value)) {
      handleChange(e);
    }
  }}
  error={!!validationErrors.phone}
  helperText={validationErrors.phone}
  placeholder={
    companyCountry.toLowerCase() === 'india'
      ? "Enter 10-digit mobile number"
      : "Enter mobile number"
  }
/>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                id="skills"
                name="skills"
                label="Skills"
                placeholder="e.g. React, JavaScript, Node.js"
                value={formData.skills}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                id="experience"
                label="Years of Experience"
                name="experience"
                type="number"
                value={formData.experience}
                onChange={handleChange}
                error={!!validationErrors.experience}
                helperText={validationErrors.experience}
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="notice-period-label">Notice Period</InputLabel>
                <Select
                  labelId="notice-period-label"
                  id="noticePeriod"
                  name="noticePeriod"
                  value={formData.noticePeriod}
                  label="Notice Period"
                  onChange={handleChange}
                >
                  <MenuItem value="">Not specified</MenuItem>
                  <MenuItem value="Immediate">Immediate</MenuItem>
                  <MenuItem value="15 days">15 days</MenuItem>
                  <MenuItem value="30 days">30 days</MenuItem>
                  <MenuItem value="60 days">60 days</MenuItem>
                  <MenuItem value="90 days">90 days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
{/*             
            <Grid item xs={12}>
              <FormControl fullWidth error={!!validationErrors.positionApplied}>
                <InputLabel id="position-applied-label">Position Applied For</InputLabel>
                <Select
                  required
                  labelId="position-applied-label"
                  id="positionApplied"
                  name="positionApplied"
                  value={formData.positionApplied}
                  label="Position Applied For"
                  onChange={handleChange}
                  disabled={loadingJobs || (jobTitle && true)}
                >
                  {loadingJobs ? (
                    <MenuItem value="">
                      <CircularProgress size={20} /> Loading positions...
                    </MenuItem>
                  ) : availableJobs.length > 0 ? (
                    availableJobs.map((job) => (
                      <MenuItem key={job.id} value={job.jobTitle}>
                        {job.jobTitle} - {job.jobId}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">No open positions available</MenuItem>
                  )}
                </Select>
                {validationErrors.positionApplied && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                    {validationErrors.positionApplied}
                  </Typography>
                )}
              </FormControl>
            </Grid> */}
            <Grid item xs={12}>
  <InfiniteJobDropdown
    value={selectedJobDetails}
    onChange={(job) => {
      if (job) {
        setSelectedJobDetails(job);
        setFormData(prev => ({
          ...prev,
          positionApplied: job.jobTitle,
          department: job.department || '',
          location: job.location || '',
          description: job.description || ''
        }));
      } else {
        setSelectedJobDetails(null);
        setFormData(prev => ({
          ...prev,
          positionApplied: '',
          department: '',
          location: '',
          description: ''
        }));
      }
    }}
    error={validationErrors.positionApplied}
    helperText={validationErrors.positionApplied}
    disabled={loadingJobs}
    jobTitle={jobTitle}
  />
</Grid>

           {/* <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="department"
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                disabled={department && true}
                error={!!validationErrors.department}
                helperText={validationErrors.department}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="location"
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                disabled={jobLocation && true}
                error={!!validationErrors.location}
                helperText={validationErrors.location}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="description"
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={description && true}
                error={!!validationErrors.description}
                helperText={validationErrors.description}
              />
            </Grid> */}

            {/* Department Field */}
<Grid item xs={12} md={6}>
  <TextField
    fullWidth
    id="department"
    label="Department"
    name="department"
    value={formData.department}
    onChange={handleChange}
    disabled
    error={!!validationErrors.department}
    helperText={validationErrors.department}

  />

</Grid>

{/* Location Field */}
<Grid item xs={12} md={6}>
  <TextField
    fullWidth
    id="location"
    label="Location"
    name="location"
    value={formData.location}
    onChange={handleChange}
    disabled
    error={!!validationErrors.location}
    helperText={validationErrors.location}

  />

</Grid>

{/* Description Field */}
<Grid item xs={12}>
  <TextField
    fullWidth
    id="description"
    label="Job Description"
    name="description"
    value={formData.description}
    onChange={handleChange}
    disabled
    error={!!validationErrors.description}
    helperText={validationErrors.description}
    multiline
    rows={4}

  />
  {/* {isFieldAutoFilled('description') && (
    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
      Auto-filled from selected position
    </Typography>
  )} */}
</Grid>

            {isReferral && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="referralreason"
                  label="Reason for Referral"
                  name="referralreason"
                  required
                  multiline
                  rows={3}
                  value={formData.referralreason}
                  onChange={handleChange}
                  error={!!validationErrors.referralreason}
                  helperText={validationErrors.referralreason || "Why do you think this candidate would be a good fit? (Minimum 10 characters)"}
                  placeholder="Why do you think this candidate would be a good fit?"
                />
              </Grid>
            )}
            
            <Grid item xs={12}>               
              <Box>                 
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Button
                    required
                    variant="outlined"
                    component="label"
                    color={fileError ? "error" : "primary"}
                    sx={{
                      width: 300,
                      borderStyle: fileError ? 'solid' : 'dashed',
                      borderWidth: 2,
                      py: 2,
                      '&:hover': {
                        borderStyle: 'solid'
                      }
                    }}
                  >
                    {file ? 'Change Resume' : 'Upload Resume'}
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.doc,.docx,.rtf,.txt,.odt"
                      onChange={handleFileChange}
                    />
                  </Button>

                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    Required: PDF, DOC, DOCX, RTF, TXT, or ODT files (Max size: 10MB)
                  </Typography>

                  {file && !fileError && (
                    <Box sx={{ 
                      mt: 1, 
                      p: 1, 
                      borderRadius: 1, 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: 300,
                      border: '1px solid',
                      borderColor: 'success.main',
                      bgcolor: 'success.light'
                    }}>
                      <Typography variant="caption" color="success.contrastText">
                        ✓ Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => {
                          setFile(null);
                          setFileError('');
                          const fileInput = document.querySelector('input[type="file"]');
                          if (fileInput) fileInput.value = '';
                        }}
                        sx={{ 
                          minWidth: 'auto', 
                          p: 0.5,
                          ml: 1,
                          color: 'success.contrastText'
                        }}
                      >
                        ✕
                      </Button>
                    </Box>
                  )}

                  {fileError && (
                    <Typography variant="caption" display="block" color="error" sx={{ mt: 1 }}>
                      {fileError}
                    </Typography>
                  )}
                </Box>          
              </Box>             
            </Grid>
          </Grid>

          {isReferral && (
            <>
              <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 2, mt: 4 }}>
                Referrer Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id="referrerName"
                    label="Your Name"
                    name="referrerName"
                    value={formData.referrerName}
                    onChange={handleChange}
                    disabled={loadingReferrerInfo}
                    error={!!validationErrors.referrerName}
                    helperText={validationErrors.referrerName || (loadingReferrerInfo ? "Loading your information..." : "")}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id="referrerEmail"
                    label="Your Email"
                    name="referrerEmail"
                    type="email"
                    value={formData.referrerEmail}
                    onChange={handleChange}
                    disabled={loadingReferrerInfo}
                    error={!!validationErrors.referrerEmail}
                    helperText={validationErrors.referrerEmail || (loadingReferrerInfo ? "Loading your information..." : "")}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    id="referrerRelation"
                    label="Relationship to Candidate"
                    name="referrerRelation"
                    value={formData.referrerRelation}
                    onChange={handleChange}
                    error={!!validationErrors.referrerRelation}
                    helperText={validationErrors.referrerRelation || "e.g. Former colleague, Friend, Professional contact (Minimum 3 characters)"}
                    placeholder="e.g. Former colleague, Friend, Professional contact"
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={loading || loadingReferrerInfo || (availableJobs.length === 0 && !loadingJobs)}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 
                (isApplication ? 'Submitting Application...' : 'Submitting Referral...') : 
                (isApplication ? 'Submit Application' : 'Submit Referral')
              }
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Referral;